import { useState } from 'react'
import {
  Button,
  Dialog,
  DialogTrigger,
  Heading,
  Popover,
  Radio,
  RadioGroup,
} from 'react-aria-components'
import { Check, Close, ExpandMore, Tune } from '@mui/icons-material'
import { modelTestCopy as copy } from './copy'
import { ModelCatalog, ModelSelection } from './types'
import './model-testing.css'

type Props = {
  selection: ModelSelection | null
  catalog: ModelCatalog
  threadError: string | null
  busy: boolean
  darkMode: boolean
  onStart: (alias: string | null) => Promise<boolean>
}

export function ModelSelector({
  selection,
  catalog,
  threadError,
  busy,
  darkMode,
  onStart,
}: Props) {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState('')
  const selectedOption = catalog.models.find(
    (option) => option.model_alias === draft,
  )
  const canStart = (!draft || selectedOption?.available === true) && !busy

  const start = async () => {
    if (canStart && (await onStart(draft || null))) setOpen(false)
  }

  return (
    <div className="model-testing-bar" data-dark={darkMode}>
      <div className="model-testing-bar-inner">
        <DialogTrigger
          isOpen={open}
          onOpenChange={(next) => {
            if (busy) return
            if (next) setDraft(selection?.modelAlias || '')
            setOpen(next)
          }}
        >
          <Button
            className="model-testing-trigger"
            isDisabled={busy}
            aria-label={`${copy.title}: ${selection?.displayName || copy.unconfirmed}`}
          >
            <Tune fontSize="small" aria-hidden="true" />
            <span className="model-testing-label">{copy.model}</span>
            <span className="model-testing-name">
              {selection?.displayName || copy.unconfirmed}
            </span>
            {selection && (
              <span className="model-testing-source">
                {selection.source === 'config' ? copy.standardShort : copy.test}
              </span>
            )}
            <ExpandMore fontSize="small" aria-hidden="true" />
          </Button>
          <Popover
            placement="bottom start"
            className="model-picker"
            data-dark={darkMode}
          >
            <Dialog className="model-picker-dialog">
              <div className="model-picker-heading">
                <div className="model-picker-title-row">
                  <Heading slot="title">{copy.title}</Heading>
                  <span className="model-testing-admin">{copy.admin}</span>
                  <Button
                    className="model-picker-close"
                    aria-label={copy.close}
                    isDisabled={busy}
                    onPress={() => setOpen(false)}
                  >
                    <Close fontSize="small" aria-hidden="true" />
                  </Button>
                </div>
                <p>{copy.description}</p>
              </div>
              <RadioGroup
                aria-label={copy.title}
                className="model-picker-options"
                value={draft}
                onChange={setDraft}
                isDisabled={busy}
              >
                <Radio
                  value=""
                  className="model-picker-option model-picker-default"
                >
                  <span className="model-picker-radio">
                    <Check aria-hidden="true" />
                  </span>
                  <span className="model-picker-option-copy">
                    <strong>{copy.standard}</strong>
                    <small>{catalog.defaultModel.displayName}</small>
                  </span>
                </Radio>
                {catalog.models.map((option) => (
                  <Radio
                    key={option.model_alias}
                    value={option.model_alias}
                    isDisabled={!option.available}
                    className="model-picker-option"
                  >
                    <span className="model-picker-radio">
                      <Check aria-hidden="true" />
                    </span>
                    <span className="model-picker-option-copy">
                      <strong>{option.display_name}</strong>
                      <small>
                        {option.provider === 'openai'
                          ? 'OpenAI'
                          : option.provider === 'mistral'
                            ? 'Mistral'
                            : option.provider}
                      </small>
                      {!option.available && (
                        <small className="model-picker-unavailable">
                          {option.unavailable_reason || copy.unavailable}
                        </small>
                      )}
                    </span>
                    {!option.available && (
                      <span className="model-picker-unavailable-tag">
                        {copy.unavailable}
                      </span>
                    )}
                  </Radio>
                ))}
              </RadioGroup>
              <div className="model-picker-footer">
                {threadError && (
                  <p className="model-testing-error" role="alert">
                    {threadError}
                  </p>
                )}
                <p>{copy.newThreadHint}</p>
                <Button
                  className="model-picker-start"
                  isDisabled={!canStart}
                  onPress={() => {
                    void start()
                  }}
                >
                  {busy ? copy.starting : copy.newConversation}
                </Button>
              </div>
            </Dialog>
          </Popover>
        </DialogTrigger>
        <span className="model-testing-admin model-testing-bar-admin">
          {copy.admin}
        </span>
      </div>
      {threadError && !open && (
        <p className="model-testing-error" role="alert">
          {threadError}
        </p>
      )}
    </div>
  )
}
